import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation
} from '@angular/core';
import {Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {
  concatElectronBuildVersion,
  StudioVersion
} from '@spout/global-any/models';
import {
  CustomFirestoreService,
  marketingDownloadMacIntel,
  marketingDownloadMacM1,
  marketingDownloadPC,
  removeTimestampCTorFromDocumentSnapshot,
  selectHasActiveSubscription,
  selectStripeCoupon,
  selectTrialDays,
  StripeCoupon
} from '@spout/global-web/data-access';
import {studioVersion} from '@spout/global-web/fns';
import {ENVIRONMENT, IEnvironmentState} from '@spout/global-web/models';

import {
  DocumentSnapshot,
  FirestoreError,
  onSnapshot
} from '@firebase/firestore';
import {BehaviorSubject, Observable, Observer, of, ReplaySubject, Subject} from 'rxjs';
import {catchError, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {UAParser} from 'ua-parser-js';
class HOMEPAGE_DOWNLOADS {
  windows: string = "";
  mac: string = "";
  macm1: string = "";
}
const devDownloads: HOMEPAGE_DOWNLOADS =
{
  windows: "https://firebasestorage.googleapis.com/v0/b/spout-dev-d0c97.appspot.com/o/installers%2FSpout%20Studio%20Dev-x64.msix?alt=media&token=c2e7e9e1-1eca-45fe-8652-1815b8c0ba54",
  mac: "https://firebasestorage.googleapis.com/v0/b/spout-dev-d0c97.appspot.com/o/installers%2Fspout-installer-intel-dev.dmg?alt=media&token=399e5a1c-c194-47d7-b21a-b6534f841f7f",
  macm1: "https://firebasestorage.googleapis.com/v0/b/spout-dev-d0c97.appspot.com/o/installers%2Fspout-installer-m1-dev.dmg?alt=media&token=000f15a7-482a-4171-96eb-48379c70ee1b"
};

const prodDownloads: HOMEPAGE_DOWNLOADS ={
  windows: "https://firebasestorage.googleapis.com/v0/b/spout-prod.appspot.com/o/installers%2FSpout%20Studio-x64.msix?alt=media&token=89e7d8af-4539-4f5a-bf16-06fcca044404",
  mac: "https://firebasestorage.googleapis.com/v0/b/spout-prod.appspot.com/o/installers%2Fspout-installer-intel.dmg?alt=media&token=3a93a88d-72fb-4bc9-9d75-cacbbd79ac0b",
  macm1: "https://firebasestorage.googleapis.com/v0/b/spout-prod.appspot.com/o/installers%2Fspout-installer-m1.dmg?alt=media&token=c79946de-607d-4260-9a55-21178f4d08da"
} as HOMEPAGE_DOWNLOADS
;
@Component({
  selector: 'spt-download-spout',
  templateUrl: './download-spout.component.html',
  styleUrls: ['./download-spout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'spt-download-spout',
    '[class.show-background]': 'showBackground === true'
  }
})
export class DownloadSpoutComponent implements OnInit, OnDestroy {
  private _onDestroy$: Subject<boolean> = new Subject();
  private ua: UAParser | undefined;
  private fbSub: (() => void) | undefined;

  private downloadPcUrl = 'installers/Spout Studio-x64.msix';
  private downloadMacIntelUrl = 'installers/spout-installer-intel.dmg';
  private downloadMacM1Url = 'installers/spout-installer-m1.dmg';

  private downloadPcUrl_Dev = 'installers/Spout Studio Dev-x64.msix';
  private downloadMacIntelUrl_Dev = 'installers/spout-installer-intel-dev.dmg';
  private downloadMacM1Url_Dev = 'installers/spout-installer-m1-dev.dmg';

  downloadMacIntelUrl$: ReplaySubject<string>;
  downloadMacM1Url$: ReplaySubject<string>;
  downloadPcUrl$: ReplaySubject<string>;
  firebaseStudioVersion$: ReplaySubject<string>;

  public downloads$: Subject<HOMEPAGE_DOWNLOADS>;
  hasSubscription$: Observable<boolean>;
  selectTrialDays$: Observable<number | null>;
  stripeCoupon$: Observable<StripeCoupon>;

  @Input() showBackground = true;

  constructor(
    private _store: Store,
    private _firestore: CustomFirestoreService,
    private _router: Router,
    @Inject(ENVIRONMENT) public environment: IEnvironmentState
  ) {
    this.ua = new UAParser(navigator.userAgent);
    this.downloads$ = new BehaviorSubject<HOMEPAGE_DOWNLOADS>(environment?.production == false?devDownloads:prodDownloads);
    this.downloadMacIntelUrl$ = new ReplaySubject<string>(1);
    this.downloadMacM1Url$ = new ReplaySubject<string>(1);
    this.downloadPcUrl$ = new ReplaySubject<string>(1);

    this.firebaseStudioVersion$ = new ReplaySubject<string>(1);

    this.hasSubscription$ = this._store.pipe(
      select(selectHasActiveSubscription)
    );

    this.selectTrialDays$ = this._store.pipe(select(selectTrialDays));
    this.stripeCoupon$ = this._store.pipe(select(selectStripeCoupon));
  }

  ngOnInit(): void {
    const _ref = this._firestore.docRef(studioVersion());

    this.fbSub = onSnapshot(
      _ref,
      (doc: DocumentSnapshot) => {
        const v: StudioVersion =
          removeTimestampCTorFromDocumentSnapshot<StudioVersion>(doc);

        if (v && v.electron) {
          this.firebaseStudioVersion$.next(
            concatElectronBuildVersion(v.electron)
          );
        }
      },
      (error: FirestoreError) => {
        console.error(error);
      }
    );

    this.getDownloadUrl(
      this.environment.production ? this.downloadPcUrl : this.downloadPcUrl_Dev
    )
      .pipe(
        takeUntil(this._onDestroy$),
        catchError((error: any) => {
          return of('');
        }),
        distinctUntilChanged()
      )
      .subscribe(pc => {
        this.downloadPcUrl$.next(pc);
      });

    this.getDownloadUrl(
      this.environment.production
        ? this.downloadMacIntelUrl
        : this.downloadMacIntelUrl_Dev
    )
      .pipe(
        takeUntil(this._onDestroy$),
        catchError((error: any) => {
          return of('');
        }),
        distinctUntilChanged()
      )
      .subscribe(macIntel => {
        this.downloadMacIntelUrl$.next(macIntel);
      });

    this.getDownloadUrl(
      this.environment.production
        ? this.downloadMacM1Url
        : this.downloadMacM1Url_Dev
    )
      .pipe(
        takeUntil(this._onDestroy$),
        catchError((error: any) => {
          return of('');
        }),
        distinctUntilChanged()
      )
      .subscribe(macM1 => {
        this.downloadMacM1Url$.next(macM1);
      });
  }

  ngOnDestroy() {
    this._onDestroy$.next(true);
    if (this.fbSub) {
      this.fbSub();
    }
  }

  navigateToSubscribe() {
    this._router.navigate(['subscribe']);
  }

  onDownloadPC() {
    this._store.dispatch(marketingDownloadPC());
  }

  onDownloadMacIntel() {
    this._store.dispatch(marketingDownloadMacIntel());
  }

  onDownloadMacM1() {
    this._store.dispatch(marketingDownloadMacM1());
  }

  getDownloadUrl(url: string): Observable<string> {
    const that = this;

    return new Observable((observer: Observer<any>) => {
      // Create a reference to the file we want to download
      const installerRef = this._firestore.getDownloadURL(url);

      // Get the download URL
      installerRef
        .then(function (url: string) {
          // Insert url into an <img> tag to 'download'
          observer.next(url);
        })
        .catch(function (error: any) {
          console.log(error);

          // A full list of error codes is available at
          // https://firebase.google.com/docs/storage/web/handle-errors
          switch (error.code) {
            case 'storage/object-not-found':
              // File doesn't exist
              break;

            case 'storage/unauthorized':
              // User doesn't have permission to access the object
              break;

            case 'storage/canceled':
              // User canceled the upload
              break;

            case 'storage/unknown':
              // Unknown error occurred, inspect the server response
              break;
          }
        });
    });
  }
}
